import { Instance, cast, destroy, getSnapshot, types } from 'mobx-state-tree'
import { isCondition, isAction } from '../../type-guards/isAction'
import { Condition } from './Condition'
import { Action } from './Action'
import { Node } from './Node'
import { NodeGroup } from './NodeGroup'

const _takeWhile = require('lodash/takeWhile')
const _flattenDeep = require('lodash/flattenDeep')

export const Workflow = types
  .model('Workflow')
  .props({
    actions: types.array(Action),
    conditions: types.array(Condition),
    nodes: types.array(Node),
    node_groups: types.optional(types.array(NodeGroup), []),
    name: types.string,
    version: types.number,
    editing: types.maybe(types.string),
    selectedAction: types.maybe(types.string),
    selectedCondition: types.maybe(types.string),
    inserting: types.maybe(types.boolean),
  })
  .views((self) => ({
    get selectedStep() {
      if (self.selectedAction) {
        // First check if it's an action (has reference_id)
        const action = self.actions.find((a) => a.reference_id === self.selectedAction)
        if (action) return action

        // If not found in actions, check if it's a Section Header (has node_id)
        const sectionHeader = self.nodes.find((n) => n.node_id === self.selectedAction)
        if (sectionHeader && sectionHeader.object_type === 'NodeGroup') {
          return sectionHeader
        }
      } else if (self.selectedCondition) {
        return self.conditions.find((c) => c.reference_id === self.selectedCondition)
      }
      return undefined
    },
    get upNext() {
      let next
      self.nodes.map((node) => {
        // Skip Section Headers
        if (node.object_type === 'NodeGroup') {
          return
        }

        node.steps.map((step) => {
          if (!next && !node.is_completed) {
            next = { node, step }
          }
        })
      })
      return next
    },
    get completed() {
      let complete = true
      self.nodes.map((node) => {
        // Skip Section Headers
        if (node.object_type === 'NodeGroup') {
          return
        }
        if (!node.is_completed) {
          complete = false
        }
      })
      return complete
    },
    get stub() {
      let stub = self.actions.find((a) => a.stub) as any
      if (!stub) {
        stub = self.conditions.find((c) => c.stub)
      }
      return stub
    },
    get valid() {
      const sectionHeaders = self.nodes.filter((node) => node.object_type === 'NodeGroup')
      return self.nodes.length >= 1 && !this.stub && sectionHeaders.length !== self.nodes.length
    },
    get currentNode() {
      return self.nodes[self.nodes.length - 1]
    },
    get stepCount() {
      let count = 0
      self.nodes.map((node) => (count += node?.steps.length))
      return count
    },
    get stepCountWithoutGroups() {
      let count = 0
      self.nodes.filter((n) => !n.isSectionHeader).map((node) => (count += node?.steps.length))
      return count
    },
    get flat() {
      return _flattenDeep(self.nodes.map((node) => node.steps))
    },
    get rootHasActions() {
      if (this.flat?.length && isCondition(this.flat[0])) {
        const n = self?.nodes.find((n) =>
          n.conditions.find((c) => c.criteria.includes(this.flat[0].reference_id)),
        )
        if (n && n?.actions?.length > 0) {
          return true
        }
      }
      return false
    },
    get hasContentBeforeCurrentNode() {
      if (self.nodes.length === 0) return false

      // If a stub exists, we're inserting/editing at the stub's position
      // Check from the stub's position, not the last node
      let checkFromIndex = self.nodes.length - 1

      if (this.stub) {
        const stubNodeIndex = self.nodes.findIndex((n) =>
          n.actions.includes(this.stub.reference_id),
        )
        if (stubNodeIndex !== -1) {
          checkFromIndex = stubNodeIndex
        }
      }

      const currentNode = self.nodes[checkFromIndex]

      // If current node itself has conditions (is a content node), content exists
      if (
        currentNode &&
        currentNode.object_type !== 'NodeGroup' &&
        currentNode.conditions.length > 0
      ) {
        return true
      }

      // Otherwise, look backwards from current position for a node with actual content (conditions)
      for (let i = checkFromIndex - 1; i >= 0; i--) {
        const node = self.nodes[i]
        // Check for non-Section-Header nodes that have conditions (actual content)
        // This excludes empty stub nodes which have no conditions
        if (node && node.object_type !== 'NodeGroup' && node.conditions.length > 0) {
          return true // Found a content node with conditions
        }
      }

      return false // No content nodes found
    },
    get hasCompleteTopicBeforeCurrentNode() {
      if (self.nodes.length === 0) return false

      // If a stub exists, we're inserting/editing at the stub's position
      // Check from the stub's position, not the last node
      let checkFromIndex = self.nodes.length - 1

      if (this.stub) {
        const stubNodeIndex = self.nodes.findIndex((n) =>
          n.actions.includes(this.stub.reference_id),
        )
        if (stubNodeIndex !== -1) {
          checkFromIndex = stubNodeIndex
        }
      }

      // Look backwards from current position for the nearest node with actual content (conditions)
      // Certifications must directly follow a "Complete Topic" node, so only the nearest
      // content node counts - an earlier "Complete Topic" further up the path does not
      for (let i = checkFromIndex; i >= 0; i--) {
        const node = self.nodes[i]
        // Section headers break the chain
        if (!node || node.object_type === 'NodeGroup') return false
        // Skip empty stub nodes which have no conditions
        if (node.conditions.length === 0) continue

        return node.conditions.some((nc) =>
          nc.criteria.some(
            (cr) =>
              self.conditions.find((c) => c.reference_id === cr)?.condition_name ===
              'complete_topic',
          ),
        )
      }

      return false // No content nodes found
    },
    get isInsertingBeforeCert() {
      // Only applies to the insert button (a stub exists)
      if (!this.stub) return false

      const stubNode = self.nodes.find((n) => n.actions.includes(this.stub.reference_id))
      if (!stubNode) return false

      const stubIndex = stubNode.actions.indexOf(this.stub.reference_id)
      return stubNode.actions
        .slice(stubIndex + 1)
        .some(
          (id) =>
            self.actions.find((a) => a.reference_id === id)?.action_type === 'award_certification',
        )
    },
    get isInsertingAfterSectionHeader() {
      if (self.nodes.length === 0) return false

      // Insert button (stub exists)
      if (this.stub) {
        // Find the node containing the stub action
        const stubNode = self.nodes.find((n) => n.actions.includes(this.stub.reference_id))

        if (!stubNode) return false

        // Check if this is a new empty node (no conditions) created after a section header
        // This happens when insertStepAfterSectionHeader creates a new node
        if (stubNode.conditions.length > 0) {
          // Stub is in an existing content node (stacking actions), not a new empty node
          return false
        }

        // Stub is in an empty node, check if previous node is a section header
        const stubNodeIndex = self.nodes.findIndex((n) => n.node_id === stubNode.node_id)

        if (stubNodeIndex === -1 || stubNodeIndex === 0) return false

        // Check if the node immediately before the stub's node is a section header
        const prevNode = self.nodes[stubNodeIndex - 1]
        return prevNode && prevNode.object_type === 'NodeGroup'
      }

      // Add step button (no stub, adding to end of path)
      // Check if the last node is a section header
      const lastNode = self.nodes[self.nodes.length - 1]
      return lastNode && lastNode.object_type === 'NodeGroup'
    },
    get sectionHeaders() {
      return self.nodes.filter((node) => node.object_type === 'NodeGroup')
    },
  }))
  .actions((self) => ({
    reorder(updated) {
      if (updated?.length === 0) {
        return
      }
      let order = 0
      updated?.map((step, idx) => {
        // Handle Section Headers - they are nodes, not conditions
        if (step && (step as any).object_type === 'NodeGroup') {
          order += 1
          const matchingNode = self.nodes.find((n) => n.node_id === (step as any).node_id)
          if (matchingNode) {
            matchingNode.setOrder(order)
          }
        } else if (isCondition(step)) {
          order += 1
          const matchingNode = self.nodes.find((n) =>
            n.conditions.find((c) => c.criteria.includes(step.reference_id)),
          ) as any

          // Collect actions immediately after this condition (stops at first non-action)
          const actions = _takeWhile(updated.slice(idx + 1), (o) => isAction(o))

          matchingNode.setActions(actions.map((a) => a.reference_id))
          matchingNode.setOrder(order)
        }
      })
      self.nodes.replace(self.nodes.sort((a, b) => a.order - b.order))
      this.recalculateGroups()
    },
    makeReferencesUnique() {
      // Update node ids
      self.nodes.map((node) => {
        const new_node_id = crypto.randomUUID()
        node.setNodeId(new_node_id)
      })
      // Update actions with new reference ids
      self.actions.map((action) => {
        const { reference_id } = action
        const new_reference_id = crypto.randomUUID()
        action.setReferenceId(new_reference_id)
        //  since we are duplicating here, just remove join actions from workflow - will be handled on publish
        if (action.action_type === 'join') {
          destroy(action)
        } else {
          self.nodes.map((node) => {
            // Otherwise find nodes where old action id is referenced and change to use the new reference_id
            const idx = node.actions.indexOf(reference_id)
            const newActions = [...node.actions]
            if (idx >= 0) {
              newActions[idx] = new_reference_id
            }
            node.setActions(cast(newActions))
          })
        }
      })
      // Update condition reference ids
      self.conditions.map((condition) => {
        const { reference_id } = condition
        const new_reference_id = crypto.randomUUID()
        self.nodes.map((node) => {
          node.conditions.map((node_condition) => {
            const idx = node_condition.criteria.indexOf(reference_id)
            const criteria = [...node_condition.criteria]
            if (idx >= 0) {
              criteria[idx] = new_reference_id
            }
            node_condition.setCriteria(cast([...criteria]))
          })
        })
        condition.setReferenceId(new_reference_id)
      })
    },
    setVersion(version: number) {
      self.version = version
    },
    setInserting(inserting: boolean) {
      self.inserting = inserting
    },
    insertStepAfterNode(node, index) {
      // create action stub
      const reference = crypto.randomUUID()
      const newAction = Action.create({
        stub: true,
        action_type: 'stub',
        description: '',
        force_evaluate: false,
        label: '',
        reference_id: reference,
        user_visible: false,
        conditions: [],
        tag_ids: [],
        recipients: [],
      })

      // don't shift join action to newly inserted node
      const nodeActions = [] as string[]
      node.actions?.map((id) => {
        const found = self.actions.find((action) => action.reference_id === id)
        if (found && found.action_type !== 'join') {
          nodeActions.push(id)
        }
      })

      const references = [reference, ...nodeActions]

      const actions = [...self.actions]
      actions.splice(index, 0, newAction)

      this.setActions(actions)
      node?.setActions(references)
    },
    insertStepAfterAction(node, step, index) {
      const reference = crypto.randomUUID()
      const newAction = Action.create({
        stub: true,
        action_type: '',
        description: '',
        force_evaluate: false,
        label: '',
        reference_id: reference,
        user_visible: false,
        conditions: [],
        tag_ids: [],
        recipients: [],
      })

      const references = [...node.actions]
      references.splice(references.indexOf(step.reference_id) + 1, 0, reference)

      const actions = [...self.actions]
      actions.splice(index, 0, newAction)

      this.setActions(actions)
      node?.setActions(references)
    },
    insertStepAfterSectionHeader(sectionHeaderNode) {
      // Create a new node with a stub action after a Section Header
      const reference = crypto.randomUUID()
      const newAction = Action.create({
        stub: true,
        action_type: 'stub',
        description: '',
        force_evaluate: false,
        label: '',
        reference_id: reference,
        user_visible: false,
        conditions: [],
        tag_ids: [],
        recipients: [],
      })

      this.addAction(newAction)

      const newNode = Node.create({
        label: '',
        actions: [reference],
        required: true,
        order: sectionHeaderNode.order + 1,
        conditions: [],
        object_type: 'Node',
        node_id: crypto.randomUUID(),
      })

      // Insert the new node right after the Section Header
      const temp = self.nodes.slice()
      const sectionHeaderIndex = temp.findIndex((n) => n.node_id === sectionHeaderNode.node_id)
      if (sectionHeaderIndex === -1) {
        console.error('Section Header not found in nodes array')
        return
      }
      temp.splice(sectionHeaderIndex + 1, 0, newNode)
      this.setNodes(temp)

      // Update order values for all nodes after insertion
      self.nodes.forEach((node, index) => {
        node.setOrder(index + 1)
      })

      this.recalculateGroups()
    },
    insertStepAfter(node, step, index) {
      if (step && (step as any).object_type === 'NodeGroup') {
        // Inserting after a Section Header - create a new node
        this.insertStepAfterSectionHeader(node)
      } else if (isAction(step)) {
        this.insertStepAfterAction(node, step, index)
      } else {
        this.insertStepAfterNode(node, index)
      }
    },
    selectStep(
      step:
        | undefined
        | Instance<typeof Action>
        | Instance<typeof Condition>
        | Instance<typeof Node>,
    ) {
      if (isAction(step)) {
        self.selectedAction = step.reference_id
        self.editing = step?.action_type
      } else if (isCondition(step)) {
        self.selectedCondition = step.reference_id
        self.editing = step?.condition_name
      } else if (step && (step as any).object_type === 'NodeGroup') {
        // Section Header node - store node_id in selectedAction field
        self.selectedAction = (step as any).node_id
        self.editing = 'section_header'
      } else {
        self.selectedAction = undefined
        self.selectedCondition = undefined
        self.editing = ''
      }
    },
    setEditing(type: string) {
      self.editing = type
    },
    setName(name: string) {
      self.name = name
    },
    handleJoinActions() {
      // Filter out Section Headers - join actions should only link regular nodes
      const regularNodes = self.nodes.filter((node) => node.object_type !== 'NodeGroup')

      regularNodes.map((node, index) => {
        node.setStartNode(false)
        node.setTerminalNode(false)

        // set start node
        if (index === 0) {
          node.setStartNode(true)
        }

        const next = regularNodes[index + 1]
        if (next) {
          // ensure the node includes its own join action
          if (node.joinAction && !node.actions.includes(node.joinAction.reference_id)) {
            node.setActions(cast([...node.actions, node.joinAction.reference_id]))
          }

          // check if node has a join action and that it links to the next node
          if (node.joinAction && node.joinAction.to_id !== next.node_id) {
            node.joinAction.setToID(next.node_id)
          } else if (!node.joinAction) {
            const reference_id = crypto.randomUUID()
            node.addAction(reference_id)
            this.addAction(
              cast({
                user_visible: false,
                action_type: 'join',
                description: 'Unlock the next node in the Path',
                force_evaluate: true,
                label: '',
                conditions: [],
                reference_id,
                from_id: node.node_id,
                to_id: next.node_id,
              }),
            )
          }
        } else {
          // no next node found (set terminal), destroy any join action found
          node.setTerminalNode(true)
          if (node.joinAction) {
            this.removeAction(node.joinAction)
          }
        }
      })
      // clean up actions
      self.actions.map((action) => {
        let included = false
        self.nodes.map((node) => {
          // mark action as included if found in a node's actions list
          if (node.actions.includes(action.reference_id)) {
            included = true
          }
        })
        if (!included) {
          // action found without node referencing it, destroy it
          destroy(action)
        }
      })
      self.actions
        .filter((a) => a.action_type === 'join')
        .map((joinAction) => {
          const node_ids = self.nodes.map((n) => n.node_id)
          if (joinAction.from_id && !node_ids.includes(joinAction.from_id)) {
            // join action found pointing from non-existent node - destroy it
            destroy(joinAction)
          }
        })
    },
    addNode(node: Instance<typeof Node>) {
      self.nodes = cast([...self.nodes, node])
      return node
    },
    setNodes(nodes: Array<Instance<typeof Node>>) {
      self.nodes.replace(nodes)
    },
    addCondition(condition: Instance<typeof Condition>) {
      const c = Condition.create(condition)
      self.conditions = cast([...self.conditions, c])
      return c.reference_id ?? ''
    },
    addAction(action: Instance<typeof Action>) {
      self.actions = cast([...self.actions, Action.create(action)])
    },
    setActions(actions: Array<Instance<typeof Action>>) {
      self.actions.replace(actions)
    },
    removeAction(action) {
      self.nodes.map((node) =>
        node.setActions(cast(node.actions.filter((id) => id !== action?.reference_id))),
      )
      destroy(action)
    },
    removeCertification(action) {
      // Destroy cert action along with its gold/silver/bronze conditions
      const tiers = action?.conditions?.flatMap((c) => c.criteria) ?? []
      self.conditions.filter((c) => tiers.includes(c.reference_id)).forEach((c) => destroy(c))
      this.removeAction(action)
    },
    removeCondition(condition) {
      self.nodes.map((node) =>
        node.setConditions(cast(node.conditions.filter((id) => id !== condition?.reference_id))),
      )
      destroy(condition)
    },
    removeNode(node) {
      // Section Headers have no actions/conditions - just destroy and recalculate
      if (node.object_type === 'NodeGroup') {
        destroy(node)
        self.nodes.map((n, index) => n.setOrder(index + 1))
        this.recalculateGroups()
        return
      }

      // ensure any referenced actions / condtions are removed at the workflow level
      node.conditions.map((condition) => {
        condition.criteria.map((criteria) => {
          const found = self.conditions.find((c) => c.reference_id === criteria)
          if (found) {
            destroy(found)
          }
        })
      })
      // if fallback node - move all actions (if applicable - certs) to the previous node/condition
      // assuming one node has one top level condition
      if (node.order > 1) {
        const fallback = self.nodes.find((n) => n.order === node.order - 1)
        const joinActions = self.actions.map((a) =>
          a.action_type === 'join' ? a.reference_id : '',
        )
        fallback?.actions.push(
          ...node.actions.filter((nodeAction) => !joinActions.includes(nodeAction)),
        )
      } else {
        node.actions.map((id) => {
          // for each action id find in workflow actions array
          const found = self.actions.find((a) => a.reference_id === id)
          // for each condition for the action found remove from workflow conditions
          found?.conditions?.map((condition) => {
            condition.criteria.map((c) => {
              const found = self.conditions.find((condition) => condition.reference_id === c)
              if (found) {
                destroy(found)
              }
            })
          })
          // remove root join action
          const join = self.actions.find(
            (action) => action.action_type === 'join' && action.from_id === node.reference_id,
          )
          if (join) {
            this.removeAction(join)
          }
          // after removing all of the action's condtions from workflow
          // remove the action itself from the workflow
          if (found) {
            destroy(found)
          }
        })
      }
      // after all conditions / actions are handled destroy the node
      destroy(node)
      // re-adjust node order values
      self.nodes.map((node, index) => node.setOrder(index + 1))
    },
    updateTopicIDs(id: number) {
      // after create topic publish request
      // find all object_type "Topic" nodes/conditions and update their object_ids
      self.nodes.map((node) => {
        if (node.object_type === 'Topic') {
          node.setObjectID(id)
        }
      })
      self.conditions.map((condition) => {
        if (condition.object_type === 'Topic') {
          condition.setObjectID(id)
        }
      })
    },
    handleStub(newNode) {
      // find parent node of stub
      const parentNode = self.nodes.find((n) => n.actions.includes(self.stub.reference_id))
      let i
      if (parentNode) {
        parentNode.actions.map((a, index) => {
          if (a === self.stub.reference_id) {
            i = index
          }
        })

        // update parent nodes actions and
        // move all actions > index of stub action into the new condition
        const actions = [...parentNode.actions].filter((action) => {
          const stub = self.actions.find((action) => action.stub)
          if (stub?.reference_id !== action) {
            return action
          }
        })
        parentNode.setActions(cast(actions.slice(0, i)))
        newNode.setActions(cast(actions.slice(i, actions.length)))

        // reorder the node / condition to come after the parent node of the stub
        const temp = self.nodes?.slice()
        if (temp) {
          temp.splice(
            self.nodes.indexOf(parentNode) + 1,
            0,
            temp.splice(self.nodes.indexOf(newNode), 1)[0],
          )
          this.setNodes(temp)
          // update order value for all nodes
          self.nodes.map((node, index) => {
            node.setOrder(index + 1)
          })
        }
      }
      this.removeAction(self.stub)

      // Clean up empty parent node if it's now empty
      // This happens when inserting a condition after a section header
      if (
        parentNode &&
        parentNode.actions.length === 0 &&
        parentNode.conditions.length === 0 &&
        parentNode.object_type !== 'NodeGroup'
      ) {
        // Remove the empty node from the nodes array
        const filteredNodes = self.nodes.filter((n) => n.node_id !== parentNode.node_id)
        this.setNodes(filteredNodes)

        // Update order values for all remaining nodes
        self.nodes.forEach((node, index) => {
          node.setOrder(index + 1)
        })

        // Recalculate groups after node removal
        this.recalculateGroups()
      }
    },
    addToGroup(group, user_visible: boolean, description: string) {
      if (self.stub) {
        self.stub.setLabel('Add User to Group')
        self.stub.setDescription(description)
        self.stub.setActionType('add_to_group')
        self.stub.setForceEvaluate(true)
        self.stub.setGroup(group)
        self.stub.setObjectID(group.id)
        self.stub.setUserVisible(user_visible)
        self.stub.setStub(false)
      } else {
        const reference_id = crypto.randomUUID()
        this.addAction(
          Action.create({
            user_visible,
            description,
            action_type: 'add_to_group',
            force_evaluate: true,
            label: 'Add User to Group',
            reference_id,
            object_id: group.id,
            conditions: [],
            group,
          }),
        )
        self.nodes[self.nodes.length - 1].addAction(reference_id)
      }
    },
    addToAssignment(assignment, user_visible: boolean, description: string) {
      if (self.stub) {
        self.stub.setLabel('Add User to Assignment')
        self.stub.setDescription(description)
        self.stub.setActionType('add_to_assignment')
        self.stub.setForceEvaluate(true)
        self.stub.setAssignment(assignment)
        self.stub.setObjectID(assignment.id)
        self.stub.setUserVisible(user_visible)
        self.stub.setStub(false)
      } else {
        const reference_id = crypto.randomUUID()
        this.addAction(
          Action.create({
            user_visible,
            description,
            action_type: 'add_to_assignment',
            force_evaluate: true,
            label: 'Add User to Assignment',
            reference_id,
            object_id: assignment.id,
            conditions: [],
            assignment,
          }),
        )
        self.nodes[self.nodes.length - 1].addAction(reference_id)
      }
    },
    untagUser(tags, user_visible: boolean, description: string) {
      if (self.stub) {
        self.stub.setLabel('Untag User')
        self.stub.setDescription(description)
        self.stub.setActionType('untag_user')
        self.stub.setForceEvaluate(true)
        self.stub.setTags(tags)
        self.stub.setTagIDs(tags.map((tag) => tag.id))
        self.stub.setUserVisible(user_visible)
        self.stub.setStub(false)
      } else {
        const reference_id = crypto.randomUUID()
        this.addAction(
          Action.create({
            action_type: 'untag_user',
            force_evaluate: true,
            user_visible,
            description,
            label: 'Untag User',
            reference_id,
            tag_ids: tags.map((tag) => tag.id),
            conditions: [],
            tags,
          }),
        )
        self.nodes[self.nodes.length - 1].addAction(reference_id)
      }
    },
    awardPoints(points: number) {
      const reference_id = crypto.randomUUID()
      this.addAction(
        Action.create({
          action_type: 'award_points',
          description: '',
          user_visible: false,
          force_evaluate: true,
          label: 'Tag User',
          reference_id,
          points,
          conditions: [],
        }),
      )
      self.nodes[self.nodes.length - 1].addAction(reference_id)
    },

    delay(delay: number, user_visible: boolean, description: string, delay_type: string) {
      // check if stub avail
      if (self.stub) {
        self.stub.setLabel('Delay')
        self.stub.setDescription(description)
        self.stub.setActionType(delay_type)
        self.stub.setForceEvaluate(false)
        self.stub.setDelay(delay)
        self.stub.setUserVisible(user_visible)
        self.stub.setStub(false)
      } else {
        const reference_id = crypto.randomUUID()
        this.addAction(
          Action.create({
            action_type: delay_type,
            description,
            user_visible,
            force_evaluate: false,
            label: 'Delay',
            reference_id,
            minutes: delay,
            conditions: [],
          }),
        )
        self.nodes[self.nodes.length - 1].addAction(reference_id)
      }
    },
    waitUntil(datetime: string, user_visible: boolean, description: string) {
      if (self.stub) {
        self.stub.setLabel('Wait Until')
        self.stub.setDescription(description)
        self.stub.setActionType('wait_until')
        self.stub.setForceEvaluate(false)
        self.stub.setWaitUntil(datetime)
        self.stub.setUserVisible(user_visible)
        self.stub.setStub(false)
      } else {
        const reference_id = crypto.randomUUID()
        this.addAction(
          Action.create({
            action_type: 'wait_until',
            description,
            user_visible,
            force_evaluate: false,
            label: 'Wait Until',
            reference_id,
            wait_until_datetime: datetime,
            conditions: [],
          }),
        )
        self.nodes[self.nodes.length - 1].addAction(reference_id)
      }
    },
    tagUser(tags, user_visible: boolean, description: string) {
      if (self.stub) {
        self.stub.setLabel('Tag User')
        self.stub.setDescription(description)
        self.stub.setActionType('tag_user')
        self.stub.setForceEvaluate(true)
        self.stub.setTags(tags)
        self.stub.setTagIDs(tags.map((tag) => tag.id))
        self.stub.setUserVisible(user_visible)
        self.stub.setStub(false)
      } else {
        const reference_id = crypto.randomUUID()
        this.addAction(
          Action.create({
            action_type: 'tag_user',
            force_evaluate: true,
            user_visible,
            description,
            label: 'Tag User',
            reference_id,
            tag_ids: tags.map((tag) => tag.id),
            conditions: [],
            tags,
          }),
        )
        self.nodes[self.nodes.length - 1].addAction(reference_id)
      }
    },
    sendMessage(
      description: string,
      recipients: number[],
      include_user: boolean,
      subject: string,
      body: string,
      template_name?: string,
    ) {
      const reference_id = crypto.randomUUID()
      this.addAction(
        Action.create({
          action_type: 'send_message',
          description,
          force_evaluate: false,
          label: 'Send Message to User',
          reference_id,
          recipients,
          include_user,
          subject,
          body,
          template_name: template_name ?? null,
          user_visible: false,
          conditions: [],
        }),
      )
      self.nodes[self.nodes.length - 1].addAction(reference_id)
    },
    viewSource(source) {
      const criteria_id = this.addCondition(
        Condition.create({
          verb: 'view',
          label: 'View Source',
          value: null,
          operator: null,
          object_id: source.id || source?.source_id,
          object_type: 'Source',
          reference_id: crypto.randomUUID(),
          condition_name: 'view_source',
          source,
        }),
      )
      const newNode = Node.create({
        label: 'View Source',
        actions: [],
        required: true,
        object_id: source.id || source?.source_id,
        order: self.nodes.length ? self.nodes.length + 1 : 1,
        conditions: [{ name: 'all', criteria: [criteria_id] }],
        object_type: 'Source',
        node_id: crypto.randomUUID(),
      })
      this.addNode(newNode)

      if (self.stub) {
        this.handleStub(newNode)
      }
    },
    scoreOverQuiz(quiz, threshold) {
      const criteria_id = this.addCondition(
        Condition.create({
          verb: 'score',
          label: `Score ${threshold}%+ on Quiz`,
          value: threshold,
          operator: threshold < 100 ? 'gte' : 'eq',
          object_type: 'Quiz',
          condition_name: `score_${threshold}_on_all_quizzes`,
          reference_id: crypto.randomUUID(),
          object_id: quiz.id,
          quiz,
        }),
      )
      const newNode = Node.create({
        label: `Score % + on Quiz`,
        node_id: crypto.randomUUID(),
        object_id: quiz.id,
        required: true,
        order: self.nodes.length ? self.nodes.length + 1 : 1,
        object_type: 'Quiz',
        conditions: [{ name: 'all', criteria: [criteria_id] }],
        actions: [],
      })
      this.addNode(newNode)

      if (self.stub) {
        this.handleStub(newNode)
      }
    },
    scoreOverTopic(topic, threshold) {
      const criteria_id = this.addCondition(
        Condition.create({
          verb: 'score',
          label: `Score ${threshold}%+ on Topic`,
          value: threshold,
          operator: threshold < 100 ? 'gte' : 'eq',
          object_type: 'Topic',
          condition_name: `score_${threshold}_on_all_quizzes`,
          reference_id: crypto.randomUUID(),
          object_id: topic.id,
          topic,
        }),
      )
      const newNode = Node.create({
        label: `Score X%+ on Topic`,
        node_id: crypto.randomUUID(),
        object_id: topic.id,
        required: true,
        order: self.nodes.length ? self.nodes.length + 1 : 1,
        object_type: 'Topic',
        conditions: [{ name: 'all', criteria: [criteria_id] }],
        actions: [],
      })
      this.addNode(newNode)

      if (self.stub) {
        this.handleStub(newNode)
      }
    },
    completeSurvey(survey) {
      const criteria_id = crypto.randomUUID()
      this.addCondition(
        Condition.create({
          verb: 'complete',
          label: 'Complete Survey',
          value: null,
          operator: null,
          object_type: 'Survey',
          condition_name: 'complete_survey',
          reference_id: criteria_id,
          object_id: survey.id,
          survey,
        }),
      )
      const newNode = Node.create({
        label: 'Complete Survey',
        node_id: crypto.randomUUID(),
        object_id: survey.id,
        required: true,
        order: self.nodes.length ? self.nodes.length + 1 : 1,
        object_type: 'Survey',
        conditions: [{ name: 'all', criteria: [criteria_id] }],
        actions: [],
      })
      this.addNode(newNode)

      if (self.stub) {
        this.handleStub(newNode)
      }
    },
    completeTopic(topic) {
      const criteria_id = crypto.randomUUID()
      this.addCondition(
        Condition.create({
          verb: 'complete',
          label: 'Complete Topic',
          value: null,
          operator: null,
          object_type: 'Topic',
          condition_name: 'complete_topic',
          reference_id: criteria_id,
          object_id: topic.id,
          topic,
        }),
      )
      const newNode = Node.create({
        label: 'Complete Topic',
        node_id: crypto.randomUUID(),
        object_id: topic.id,
        required: true,
        order: self.nodes.length ? self.nodes.length + 1 : 1,
        object_type: 'Topic',
        conditions: [{ name: 'all', criteria: [criteria_id] }],
        actions: [],
      })
      this.addNode(newNode)

      if (self.stub) {
        this.handleStub(newNode)
      }
    },
    completeQuiz(quiz: any) {
      const criteria_id = this.addCondition(
        Condition.create({
          verb: 'complete',
          label: 'Complete Quiz',
          value: null,
          operator: null,
          object_type: 'Quiz',
          condition_name: 'complete_quiz',
          reference_id: crypto.randomUUID(),
          object_id: quiz.id,
          quiz,
        }),
      )
      const newNode = Node.create({
        label: 'Complete Quiz',
        node_id: crypto.randomUUID(),
        object_id: quiz.id,
        required: true,
        order: self.nodes.length ? self.nodes.length + 1 : 1,
        object_type: 'Quiz',
        conditions: [{ name: 'all', criteria: [criteria_id] }],
        actions: [],
      })
      this.addNode(newNode)

      if (self.stub) {
        this.handleStub(newNode)
      }
    },
    awardBadge(badge: any, user_visible: boolean, description: string, topic_id?: number) {
      // Ensure at least one complete topic node exists

      if (self.nodes.length === 0) {
        const criteria_id = this.addCondition(
          Condition.create({
            verb: 'complete',
            label: 'Complete Topic',
            value: null,
            operator: null,
            object_type: 'Topic',
            condition_name: 'complete_topic',
            reference_id: crypto.randomUUID(),
            object_id: topic_id,
          }),
        )
        this.addNode(
          Node.create({
            label: 'Complete Topic',
            node_id: crypto.randomUUID(),
            object_id: topic_id,
            required: true,
            order: self.nodes.length ? self.nodes.length + 1 : 1,
            object_type: 'Topic',
            conditions: [{ name: 'all', criteria: [criteria_id] }],
            actions: [],
          }),
        )
      }

      if (self.stub) {
        self.stub.setLabel('Award Badge')
        self.stub.setDescription(description)
        self.stub.setActionType('award_badge')
        self.stub.setForceEvaluate(false)
        self.stub.setObjectID(badge.id)
        self.stub.setBadge(badge)
        self.stub.setUserVisible(user_visible)
        self.stub.setStub(false)
      } else {
        // Add an action referencing the complete topic condition reference ids as criteria
        const action_id = crypto.randomUUID()
        this.addAction(
          Action.create({
            action_type: 'award_badge',
            description,
            force_evaluate: false,
            label: 'Award Badge',
            reference_id: action_id,
            object_id: badge.id,
            user_visible,
            conditions: [],
            badge,
          }),
        )
        self.nodes[self.nodes.length - 1].addAction(action_id)
      }
    },
    updateCertification(
      step: any,
      title: string,
      user_visible: boolean,
      description: string,
      certification?: any,
      topic_id?: number,
    ) {
      step.setDescription(description)
      step.setLabel(title)
      step.setUserVisible(user_visible)

      if (certification) {
        step.setObjectID(certification.certification_id)
        step.conditions.map((condition) => {
          condition.criteria.map((criteria, index) => {
            const found = self.conditions.find((c) => c.reference_id === criteria)
            if (found) {
              found.updateLevelId(
                certification.levels.find((level) => level.order === index + 1).level_id,
              )
            }
          })
        })
      }
    },
    awardCertification(
      title: string,
      certification: any,
      user_visible: boolean,
      description: string,
      topic_id?: number,
    ) {
      // ensure at least one node exists to attach the certification
      let node: any = self.nodes[self.nodes.length - 1]

      // Inserting between steps - attach to the node holding the stub, not the last node
      const stubNode = self.stub
        ? self.nodes.find((n) => n.actions.includes(self.stub.reference_id))
        : undefined
      if (stubNode) {
        node = stubNode
        topic_id = stubNode.object_id ?? topic_id
      }

      if (self.nodes.length === 0) {
        // Automatically add base topic completion condition to trigger cert action
        const criteria_id = crypto.randomUUID()
        this.addCondition(
          Condition.create({
            verb: 'complete',
            label: 'Complete Topic',
            value: null,
            operator: null,
            object_type: 'Topic',
            object_id: topic_id, // defaults to undefined for new topics
            condition_name: 'complete_topic',
            reference_id: criteria_id,
          }),
        )
        // Add node referencing complete topic condition + award cert action and tick order
        node = Node.create({
          label: 'Complete Topic',
          node_id: crypto.randomUUID(),
          required: true,
          order: self.nodes.length ? self.nodes.length + 1 : 1,
          object_type: 'Topic',
          object_id: topic_id,
          conditions: [{ name: 'all', criteria: [criteria_id] }],
          actions: [],
        })
        this.addNode(node)
      }

      // Create the three conditions for default award certification action
      // for now gold/silver/bronze
      const gold = crypto.randomUUID()
      const silver = crypto.randomUUID()
      const bronze = crypto.randomUUID()

      this.addCondition(
        Condition.create({
          verb: 'score',
          label: 'Score 100% on All Quizzes',
          value: 100,
          operator: 'eq',
          object_type: 'Topic',
          object_id: topic_id,
          condition_name: 'score_100_on_all_quizzes',
          certification_level_id: certification.levels.find((level) => level.order === 1).level_id,
          reference_id: gold,
        }),
      )

      this.addCondition(
        Condition.create({
          verb: 'score',
          label: 'Score 90% + on All Quizzes',
          value: 90,
          operator: 'gte',
          object_type: 'Topic',
          object_id: topic_id,
          reference_id: silver,
          condition_name: 'score_90_on_all_quizzes',
          certification_level_id: certification.levels.find((level) => level.order === 2).level_id,
        }),
      )

      this.addCondition(
        Condition.create({
          verb: 'score',
          label: 'Score 80% + on All Quizzes',
          value: 80,
          operator: 'gte',
          object_type: 'Topic',
          object_id: topic_id,
          condition_name: 'score_80_on_all_quizzes',
          certification_level_id: certification.levels.find((level) => level.order === 3).level_id,
          reference_id: bronze,
        }),
      )

      // Must descend gold -> bronze - implicit ordering for backend
      const conditions = [{ name: 'any', criteria: [gold, silver, bronze] }]

      if (stubNode) {
        // Convert the stub in place so the cert keeps the inserted position
        self.stub.setLabel(title)
        self.stub.setDescription(description)
        self.stub.setActionType('award_certification')
        self.stub.setForceEvaluate(true)
        self.stub.setObjectID(certification.certification_id)
        self.stub.setUserVisible(user_visible)
        self.stub.setConditions(conditions)
        self.stub.setStub(false)
        return
      }

      // Add an action referencing the previous condition reference ids as criteria
      const action_id = crypto.randomUUID()
      this.addAction(
        Action.create({
          action_type: 'award_certification',
          force_evaluate: true,
          label: title,
          reference_id: action_id,
          user_visible,
          description,
          object_id: certification.certification_id,
          conditions,
        }),
      )
      node.addAction(action_id)
    },
    setNodeGroups(groups: Instance<typeof NodeGroup>[]) {
      self.node_groups.replace(groups)
    },
    recalculateGroups() {
      const groups: Array<{ node_group_id: string; node_ids: string[] }> = []
      let currentGroup: { node_group_id: string; node_ids: string[] } | null = null

      // Iterate nodes in order
      self.nodes
        .slice()
        .sort((a, b) => a.order - b.order)
        .forEach((node) => {
          if (node.object_type === 'NodeGroup') {
            // Save previous group (even if empty - don't filter)
            if (currentGroup) {
              groups.push(currentGroup)
            }
            // Start new group - node.label was set by user in the add/update sidebar
            currentGroup = {
              node_group_id: node.label,
              node_ids: [],
            }
          } else if (currentGroup) {
            // Add regular node to current group
            currentGroup.node_ids.push(node.node_id)
          }
          // Nodes before first header remain ungrouped
        })

      // Add final group (even if empty)
      if (currentGroup) {
        groups.push(currentGroup)
      }

      this.setNodeGroups(groups.map((g) => NodeGroup.create(g)) as any)
    },
    addSectionHeader(label: string, afterNode?: Instance<typeof Node>) {
      const node_id = crypto.randomUUID()
      const newSectionHeader = Node.create({
        label,
        node_id,
        object_type: 'NodeGroup',
        actions: [],
        conditions: [],
        required: false,
        order: self.nodes.length + 1,
      })

      if (self.stub) {
        // Find the node containing the stub action
        const parentNode = self.nodes.find((n) => n.actions.includes(self.stub.reference_id))
        if (parentNode) {
          // Get the node's position
          const nodeIndex = self.nodes.indexOf(parentNode)

          // Remove stub action from parent node
          parentNode.setActions(
            cast(parentNode.actions.filter((id) => id !== self.stub.reference_id)),
          )

          // Insert Section Header at the stub's position
          const temp = [...self.nodes]

          // If parent node only had the stub (no other actions/conditions), replace it with Section Header
          if (parentNode.actions.length === 0 && parentNode.conditions.length === 0) {
            temp.splice(nodeIndex, 1, newSectionHeader)
          } else {
            // Otherwise insert Section Header after parent node
            temp.splice(nodeIndex + 1, 0, newSectionHeader)
          }

          this.setNodes(temp)
          self.nodes.forEach((node, idx) => node.setOrder(idx + 1))

          // Remove the stub action
          destroy(self.stub)
        } else {
          // Fallback: stub not found in any node, just add to end
          this.addNode(newSectionHeader)
        }
      } else if (afterNode) {
        const index = self.nodes.indexOf(afterNode)
        const temp = [...self.nodes]
        temp.splice(index + 1, 0, newSectionHeader)
        this.setNodes(temp)
        self.nodes.forEach((node, idx) => node.setOrder(idx + 1))
      } else {
        this.addNode(newSectionHeader)
      }

      this.recalculateGroups()
      return newSectionHeader
    },
    updateSectionHeader(node: Instance<typeof Node>, newLabel: string) {
      if (node.object_type !== 'NodeGroup') {
        throw new Error('Can only update Section Header nodes')
      }
      node.setLabel(newLabel)
      this.recalculateGroups()
    },
    hydrateNodeGroups() {
      if (!self.node_groups || self.node_groups.length === 0) return

      // Remove any existing Section Header nodes first
      const sectionHeaders = self.nodes.filter((n) => n.object_type === 'NodeGroup')
      sectionHeaders.forEach((n) => destroy(n))

      // For each node_group, insert a Section Header node before its first member
      self.node_groups.forEach((group) => {
        if (group.node_ids.length === 0) {
          // Empty group - add at the end
          this.addNode(
            Node.create({
              label: group.node_group_id,
              node_id: `${Math.random()}`,
              object_type: 'NodeGroup',
              actions: [],
              conditions: [],
              required: false,
              order: self.nodes.length + 1,
            }),
          )
        } else {
          // Find first node in group
          const firstNodeId = group.node_ids[0]
          const firstNode = self.nodes.find((n) => n.node_id === firstNodeId)

          if (firstNode) {
            // Insert Section Header before first node
            const index = self.nodes.indexOf(firstNode)
            const temp = [...self.nodes]
            temp.splice(
              index,
              0,
              Node.create({
                label: group.node_group_id,
                node_id: `${Math.random()}`,
                object_type: 'NodeGroup',
                actions: [],
                conditions: [],
                required: false,
                order: firstNode.order,
              }),
            )
            this.setNodes(temp)
          }
        }
      })

      // Recalculate order values
      self.nodes
        .slice()
        .sort((a, b) => a.order - b.order)
        .forEach((node, idx) => node.setOrder(idx + 1))
    },
    prepareForPublish() {
      // Build node_groups array from current Section Header positions
      this.recalculateGroups()

      // Filter out Section Header display nodes
      const regularNodes = self.nodes.filter((node) => node.object_type !== 'NodeGroup')

      // CRITICAL: Actually remove Section Header nodes from the array
      self.nodes.replace(regularNodes)

      // Recalculate order values for remaining nodes (1, 2, 3...)
      self.nodes.forEach((node, idx) => node.setOrder(idx + 1))

      // Handle join actions and cleanup
      this.handleJoinActions()

      // Lastly, recalculate order values for remaining actions (1, 2, 3...)
      self.nodes.forEach((node, idx) => {
        node.setOrder(idx + 1)
        node.actions.map((actionRef, idx2) => {
          const a = self.actions.find((action) => action.reference_id === actionRef)
          if (a) {
            a.setActionOrder(idx2 + 1)
          }
        })
      })
    },
  }))
